import { useEffect, useState } from 'react';

const breakpoints = {
   sm: 640,
   md: 768,
   lg: 1024,
   xl: 1280,
   '2xl': 1536,
} as const;

type Breakpoint = keyof typeof breakpoints;

/**
 * Hook to check if a media query matches
 * @param query - Media query string
 * @returns boolean indicating if the query matches (undefined during SSR)
 */
export function useMediaQuery(query: string): boolean | undefined {
   const [matches, setMatches] = useState<boolean | undefined>();

   useEffect(() => {
      const mediaQuery = window.matchMedia(query);

      setMatches(mediaQuery.matches);

      const handleChange = (event: MediaQueryListEvent) => {
         setMatches(event.matches);
      };

      mediaQuery.addEventListener('change', handleChange);

      return () => {
         mediaQuery.removeEventListener('change', handleChange);
      };
   }, [query]);

   return matches;
}

/**
 * Hook to check if viewport is at or above a breakpoint
 * @param breakpoint - Tailwind breakpoint name
 * @returns boolean indicating if viewport is at or above the breakpoint
 */
export function useBreakpoint(breakpoint: Breakpoint): boolean | undefined {
   const query = `(min-width: ${breakpoints[breakpoint]}px)`;
   return useMediaQuery(query);
}

/**
 * Hook that returns different values based on current breakpoint using mobile-first cascade
 * @param values - Object with breakpoint keys and corresponding values
 * @param defaultValue - Default value to use during SSR
 */
export function useBreakpointValue<T>(
   values: Partial<Record<Breakpoint | 'base', T>>,
   defaultValue?: T
): T | undefined {
   const sm = useBreakpoint('sm');
   const md = useBreakpoint('md');
   const lg = useBreakpoint('lg');
   const xl = useBreakpoint('xl');
   const xxl = useBreakpoint('2xl');

   if (sm === undefined) return defaultValue;

   let result = defaultValue;

   if ('base' in values) result = values.base;
   if (sm && 'sm' in values) result = values.sm;
   if (md && 'md' in values) result = values.md;
   if (lg && 'lg' in values) result = values.lg;
   if (xl && 'xl' in values) result = values.xl;
   if (xxl && '2xl' in values) result = values['2xl'];

   return result;
}
